import {
  type WorkspaceAgentMentionItem,
  type WorkspaceAgentMentionTreeItem,
} from '~/components/v2/components/workspace/workspace-agent-panel/mentions/types';
import { isWorkspaceAgentMentionTreeItem } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/workspaceAgentMentionItems';

/** Whether each tree row was open at the last commit, so a toggle can be announced as one. */
export type WorkspaceAgentMentionExpansionState = Record<string, boolean>;

export const EMPTY_WORKSPACE_AGENT_MENTION_EXPANSION_STATE: WorkspaceAgentMentionExpansionState = {};

export const EMPTY_WORKSPACE_AGENT_MENTION_PENDING_COUNT_NODE_IDS: readonly string[] = [];

/** Rows one level under the row at `index`, up to where that row's run ends. */
export const countWorkspaceAgentMentionChildRows = (items: WorkspaceAgentMentionItem[], index: number) => {
  const row = items[index];

  if (!row || !isWorkspaceAgentMentionTreeItem(row)) {
    return 0;
  }

  let count = 0;

  for (const item of items.slice(index + 1)) {
    if (!isWorkspaceAgentMentionTreeItem(item) || item.depth <= row.depth) {
      break;
    }

    if (item.depth === row.depth + 1) {
      count += 1;
    }
  }

  return count;
};

/** A library holds boards; a board holds sub-boards. Either way the count is spoken, not implied. */
export const toWorkspaceAgentMentionChildCountLabel = (item: WorkspaceAgentMentionTreeItem, count: number) => {
  const noun = item.kind === 'library' ? 'board' : 'sub-board';

  return `${count} ${noun}${count === 1 ? '' : 's'}`;
};

/** A row announced open before its children arrived is still owed its count. */
export type WorkspaceAgentMentionExpansionAnnouncement = {
  message: string;
  pendingCountNodeIds: string[];
};

export type ToWorkspaceAgentMentionExpansionAnnouncementParams = {
  /** Only this row's own children can say whether it is still uncounted. */
  hasPendingChildCount: (item: WorkspaceAgentMentionTreeItem) => boolean;
  items: WorkspaceAgentMentionItem[];
  /** Rows announced open at an earlier commit whose count has yet to be spoken. */
  pendingCountNodeIds: readonly string[];
  previousExpansion: WorkspaceAgentMentionExpansionState;
};

/**
 * A row the user just opened or closed, announced in place of the total — the count did change,
 * but what they asked for was this row, and a re-read of the total does not say it happened.
 *
 * A row opened onto children still being fetched is announced as open with no count, since zero is
 * not the answer; the count follows once they land. A row whose children failed is announced the
 * same way and stays owed — the rows it holds are still unknown, and the source's own error state
 * is what speaks to the failure.
 */
export const toWorkspaceAgentMentionExpansionAnnouncement = ({
  hasPendingChildCount,
  items,
  pendingCountNodeIds,
  previousExpansion,
}: ToWorkspaceAgentMentionExpansionAnnouncementParams): WorkspaceAgentMentionExpansionAnnouncement => {
  let message = '';
  const nextPendingCountNodeIds: string[] = [];

  items.forEach((item, index) => {
    if (!isWorkspaceAgentMentionTreeItem(item)) {
      return;
    }

    const wasExpanded = previousExpansion[item.id];
    const hasToggled = wasExpanded !== undefined && wasExpanded !== item.isExpanded;
    const isOwedCount = pendingCountNodeIds.includes(item.id);

    if (!hasToggled && !isOwedCount) {
      return;
    }

    if (!item.isExpanded) {
      if (hasToggled) {
        message = `${item.title} collapsed`;
      }

      return;
    }

    if (hasPendingChildCount(item)) {
      nextPendingCountNodeIds.push(item.id);

      if (hasToggled) {
        message = `${item.title} expanded`;
      }

      return;
    }

    message = `${item.title} expanded, ${toWorkspaceAgentMentionChildCountLabel(
      item,
      countWorkspaceAgentMentionChildRows(items, index),
    )}`;
  });

  return { message, pendingCountNodeIds: nextPendingCountNodeIds };
};

export const toWorkspaceAgentMentionExpansionState = (
  items: WorkspaceAgentMentionItem[],
): WorkspaceAgentMentionExpansionState =>
  Object.fromEntries(items.filter(isWorkspaceAgentMentionTreeItem).map((item) => [item.id, item.isExpanded]));
